import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Search, Image as ImageIcon, Check } from 'lucide-react';
import './MediaPicker.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MediaPicker = ({ value, onChange, type = 'IMAGE', token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  // Загрузка списка медиафайлов
  const { data: mediaFiles = [], isLoading } = useQuery({
    queryKey: ['adminMediaPicker', token],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.media || [];
    },
    enabled: isOpen,
  });

  // Фильтрация медиафайлов
  const filteredMedia = mediaFiles.filter(file => {
    if (!searchQuery) return true;
    const name = (file.filename || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  // Загрузка файла
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await axios.post(
        `${API_URL}/admin/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminMediaPicker', token] });
      const m = data.media;
      if (m && m.url) {
        onChange(m.url);
      }
      setIsOpen(false);
    },
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      uploadMutation.mutate(file, {
        onSettled: () => setUploading(false)
      });
    }
  };

  const handleSelectMedia = (mediaUrl) => {
    onChange(mediaUrl);
    setIsOpen(false);
  };

  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL.replace('/api', '')}${url}`;
  };

  return (
    <div className="media-picker">
      <div className="media-picker-preview" onClick={() => setIsOpen(true)}>
        {value ? (
          <div className="media-preview">
            <img src={getMediaUrl(value)} alt="Preview" />
            <div className="media-preview-overlay">
              <span>Изменить</span>
            </div>
          </div>
        ) : (
          <div className="media-picker-placeholder">
            <ImageIcon size={32} />
            <span>Выбрать изображение</span>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="media-picker-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              className="media-picker-content"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="media-picker-header">
                <h3>Выбор медиафайла</h3>
                <button
                  className="close-btn"
                  onClick={() => setIsOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="media-picker-body">
                <div className="media-upload-section">
                  <label className="upload-label">
                    <Upload size={20} />
                    <span>Загрузить новый файл</span>
                    <input
                      type="file"
                      accept={type === 'IMAGE' ? 'image/*' : 'video/*'}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      disabled={uploading}
                    />
                  </label>
                  {uploading && (
                    <div className="upload-progress">
                      <div className="spinner-small"></div>
                      <span>Загрузка...</span>
                    </div>
                  )}
                </div>

                <div className="media-search">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Поиск по имени файла..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="media-grid">
                  {isLoading ? (
                    <div className="loading-state">Загрузка...</div>
                  ) : filteredMedia.length === 0 ? (
                    <div className="empty-state">Медиафайлы не найдены</div>
                  ) : (
                    filteredMedia.map((file) => {
                      const isSelected = value === file.url;
                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.url || '');
                      return (
                        <motion.div
                          key={file.id}
                          className={`media-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSelectMedia(file.url)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {isImage ? (
                            <img src={getMediaUrl(file.url)} alt={file.filename || ''} />
                          ) : (
                            <video src={getMediaUrl(file.url)} />
                          )}
                          {isSelected && (
                            <div className="selected-badge">
                              <Check size={16} />
                            </div>
                          )}
                          <div className="media-item-name">
                            {file.filename || file.url}
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaPicker;
